import React, { useState, useEffect, useContext } from "react";
import { VocabularyContext } from "../Contexts/VocabularyContext";
import VocabularyPopup from "./PageNotePad/VocabularyPopup";

const VocabularyLookupWrapper = ({ children }) => {
  const [selectedWord, setSelectedWord] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [wordExists, setWordExists] = useState(false);
  const { saveWordToNotepad, isWordInVocabulary } =
    useContext(VocabularyContext);

  useEffect(() => {
    // Double-click is the only way to look a word up, so the listener must not
    // depend on the role being present when this component mounts: after a login
    // the layout is already mounted, so the previous role check meant the
    // listener was never attached at all until a manual page reload. What was
    // double-clicked is checked in the handler instead.
    document.addEventListener("dblclick", handleDoubleClick);

    return () => {
      document.removeEventListener("dblclick", handleDoubleClick);
    };
  }, []);

  const handleDoubleClick = (event) => {
    // Don't capture double-clicks in input fields, textareas, etc.
    if (
      event.target.tagName.toLowerCase() === "input" ||
      event.target.tagName.toLowerCase() === "textarea" ||
      event.target.isContentEditable ||
      event.target.closest(".vocabulary-popup") // Don't trigger inside popup itself
    ) {
      return;
    }
    // Get selected text
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    // Only proceed if there's actual text selected
    if (selectedText && selectedText.length > 0) {
      // Simple word extraction - get first word if multiple words are selected
      const word = selectedText
        .split(/\s+/)[0]
        .toLowerCase()
        .replace(/[^\w-]/g, "");
      if (word && word.length > 0) {
        setSelectedWord(word);
        setShowPopup(true);

        // Check if word already exists in vocabulary
        const exists = isWordInVocabulary(word);
        setWordExists(exists);

        // Calculate position for popup (near mouse but not directly under it)
        const x = Math.min(
          event.clientX - 50,
          window.innerWidth - 500 // Ensure popup doesn't go off screen
        );
        const y = Math.min(
          event.clientY + 10,
          window.innerHeight - 550 // Ensure popup doesn't go off screen
        );

        setPosition({ x, y });

        // Prevent default actions and stop propagation
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedWord(null);
  };

  // Returns whether the word really reached the vocabulary, so the popup can stop
  // showing "Already Added" for saves that never happened. The popup stays open
  // either way: closing it here made the meaning flash for a fraction of a second,
  // because the word is saved the moment a definition arrives.
  const saveWord = async (word, definition, partOfSpeech, pronunciation) => {
    try {
      const saved = await saveWordToNotepad(
        word,
        definition,
        partOfSpeech,
        pronunciation
      );
      if (saved) {
        console.log(`Word "${word}" added to vocabulary`);
      } else {
        console.warn(`Word "${word}" could not be saved`);
      }
      return saved === true;
    } catch (error) {
      console.error("Error saving word:", error);
      return false;
    }
  };

  return (
    <>
      {children}{" "}
      {showPopup && selectedWord && (
        <VocabularyPopup
          word={selectedWord}
          onClose={closePopup}
          onSave={saveWord}
          position={position}
          wordExists={wordExists}
        />
      )}
    </>
  );
};

export default VocabularyLookupWrapper;
